import { useEffect, useState } from 'react'

import { API } from '@/api'
import { TeamItem } from '@/api/type-response'
import { Loading } from '@/components/base/Loading'
import { Modal } from '@/components/base/Modal'
import { ItemPriceType } from '@/consts/item'
import { ORDER_CATALOG } from '@/consts/order'
import { QuotaType } from '@/consts/team'
import { Team } from '@/models/team'

import { CreateTeamModal, TeamPlan } from './CreateTeamModal'
import { ManagePlanCheckoutModal } from './ManagePlanCheckoutModal'
import { ManagePlanSuccessModal } from './ManagePlanSuccessModal'

type Step = 'select-plan' | 'checkout' | 'success'

interface TeamManagePlanFlowProps {
  open: boolean
  team: Team
  onClose: () => void
  onSuccess: () => void
}

// Helper to extract quota value
function getQuotaValue(
  quotaLimits: Array<{ type: number; upperLimit: string }> | undefined,
  quotaType: QuotaType,
): number {
  const quota = quotaLimits?.find((q) => q.type === quotaType)
  return parseInt(quota?.upperLimit ?? '0', 10)
}

// Transform API items to TeamPlan with upgradePrice
function transformItemsWithUpgrade(items: TeamItem[]): TeamPlan[] {
  return items
    .map((item) => {
      const limit = item.desc.team.limit
      const quotaLimits = limit.quotaLimits
      const priceCent = item.prices[0]?.priceCent ?? 0
      const upgradePrice = item.prices.find((p) => p.type === ItemPriceType.Upgrade)?.priceCent

      return {
        id: item.resID,
        name: item.desc.team.name || (priceCent === 0 ? 'Free' : 'Plan'),
        pricePerMonth: priceCent,
        upgradePrice,

        membersNumLimit: parseInt(limit.membersNumLimit, 10),
        adminsNumLimit: parseInt(limit.adminsNumLimit ?? '0', 10),
        sharedSpaceLimit: parseInt(limit.sharedSpaceLimit, 10),
        memberSpaceLimit: getQuotaValue(quotaLimits, QuotaType.StorageBytes),
        snapshotResumeLimit: limit.snapshotResumeLimit,

        instanceCount: getQuotaValue(quotaLimits, QuotaType.InstanceCount),
        snapshotCount: getQuotaValue(quotaLimits, QuotaType.SnapshotCount),
        imageCount: getQuotaValue(quotaLimits, QuotaType.ImageCount),
        autoScaling: getQuotaValue(quotaLimits, QuotaType.AutoScaling),
        autoDeployment: getQuotaValue(quotaLimits, QuotaType.AutoDeployment),
        meshNetCount: getQuotaValue(quotaLimits, QuotaType.MeshNetCount),
      }
    })
    .sort((a, b) => a.pricePerMonth - b.pricePerMonth)
}

export function TeamManagePlanFlow({ open, team, onClose, onSuccess }: TeamManagePlanFlowProps) {
  const [step, setStep] = useState<Step>('select-plan')
  const [selectedPlan, setSelectedPlan] = useState<TeamPlan | null>(null)
  const [plans, setPlans] = useState<TeamPlan[]>([])
  const [currentPlanResId, setCurrentPlanResId] = useState<string>('')
  const [currentPlanPrice, setCurrentPlanPrice] = useState<number>(0)
  const [isLoadingItems, setIsLoadingItems] = useState(false)
  const [updatedTeam, setUpdatedTeam] = useState<Team | null>(null)
  const orderId = team.order?.id
  const currentItemId = team.order?.itemID
  const isExpired = team.isExpired

  // Active orders need upgrade prices; expired orders use standard purchase prices.
  useEffect(() => {
    if (open && orderId) {
      setIsLoadingItems(true)
      API.getItems({
        catalog: ORDER_CATALOG.TEAM,
        page: 1,
        per_page: 100,
        ...(isExpired ? {} : { from_order: true, from_order_id: orderId }),
      })
        .then((res) => {
          const teamItems = res.items.filter(
            (item): item is TeamItem => item.catalog === ORDER_CATALOG.TEAM,
          )
          setPlans(transformItemsWithUpgrade(teamItems))

          // Find current plan by matching itemID
          const currentItem = teamItems.find((item) => item.id === currentItemId)
          if (currentItem) {
            setCurrentPlanResId(currentItem.resID)
            setCurrentPlanPrice(currentItem.prices[0]?.priceCent ?? 0)
          }
        })
        .catch((err) => {
          console.error('Failed to fetch items:', err)
        })
        .finally(() => {
          setIsLoadingItems(false)
        })
    }
  }, [currentItemId, isExpired, open, orderId])

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep('select-plan')
      setSelectedPlan(null)
      setUpdatedTeam(null)
    }
  }, [open])

  // Step 1: Plan selected -> go directly to checkout
  const handlePlanSelected = (plan: TeamPlan) => {
    setSelectedPlan(plan)
    setStep('checkout')
  }

  // Navigation: back from checkout to plan select
  const handleCheckoutBack = () => {
    setStep('select-plan')
  }

  // Checkout success: fetch updated team info and go to success step
  const handleCheckoutSuccess = async () => {
    try {
      // Use getTeamList to get complete team data including order info
      const teamsRes = await API.getTeamList({ page: 1, per_page: 100 })
      const teams = teamsRes.teams.map((item) => new Team(item))
      const newTeam = teams.find((t) => t.resID === team.resID)
      setUpdatedTeam(newTeam ?? team)
      setStep('success')
    } catch (error) {
      console.error('Failed to fetch updated team info:', error)
      // Still go to success step with original team data
      setUpdatedTeam(team)
      setStep('success')
    }
  }

  // Success modal close: call parent onSuccess to refresh data
  const handleSuccessClose = () => {
    onSuccess()
  }

  // Show loading while fetching items
  if (isLoadingItems) {
    return (
      <Modal open={open} onClose={onClose} className="w-[400px] h-[300px]">
        <Modal.Body className="flex items-center justify-center h-full">
          <Loading />
        </Modal.Body>
      </Modal>
    )
  }

  return (
    <>
      {/* Step 1: Select Plan */}
      <CreateTeamModal
        open={open && step === 'select-plan'}
        onClose={onClose}
        onPlanSelected={handlePlanSelected}
        currentPlanId={currentPlanResId}
        currentPlanPrice={currentPlanPrice}
        isCurrentPlanExpired={isExpired}
        plans={plans}
        mode="manage"
      />

      {/* Step 2: Checkout */}
      {selectedPlan && (
        <ManagePlanCheckoutModal
          open={open && step === 'checkout'}
          onClose={onClose}
          onBack={handleCheckoutBack}
          onSuccess={handleCheckoutSuccess}
          team={team}
          selectedPlan={selectedPlan}
          currentPlanId={currentPlanResId}
        />
      )}

      {/* Step 3: Success */}
      {selectedPlan && updatedTeam && (
        <ManagePlanSuccessModal
          open={open && step === 'success'}
          onClose={handleSuccessClose}
          team={updatedTeam}
          selectedPlan={selectedPlan}
        />
      )}
    </>
  )
}
